import { EduContentTOCInterface } from '@campus/dal';
import { Dictionary } from '@ngrx/entity';

/**
 * One palette application the directive will commit to the DOM. The directive
 * resolves a list of these from its inputs, then maps each to a `scoped-palette-*`
 * class on the host (or body) and asks `StyleInjectorService` to inject the
 * corresponding stylesheet.
 *
 * `depth` only matters for `eduContentTOC` / `learningDomain` — it drives the
 * selector repetition so a deeper toc (and its LD) outranks a shallower one.
 * Method/book always use 0.
 */
export interface ScopeApplication {
  kind: 'method' | 'eduContentBook' | 'eduContentTOC' | 'learningDomain';
  id: number;
  depth: number;
}

export function applicationsEqual(a: readonly ScopeApplication[], b: readonly ScopeApplication[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i].kind !== b[i].kind || a[i].id !== b[i].id || a[i].depth !== b[i].depth) return false;
  }
  return true;
}

// In non-walking modes, just emit one application per explicit id, all at
// depth 0 — preserves the legacy single-toc cascade for `manual` consumers.
export function buildExplicitApplications(
  methodId: number | undefined,
  bookId: number | undefined,
  learningDomainId: number | undefined,
  tocId: number | undefined
): ScopeApplication[] {
  const apps: ScopeApplication[] = [];
  if (methodId != null) apps.push({ kind: 'method', id: methodId, depth: 0 });
  if (bookId != null) apps.push({ kind: 'eduContentBook', id: bookId, depth: 0 });
  if (learningDomainId != null) apps.push({ kind: 'learningDomain', id: learningDomainId, depth: 0 });
  if (tocId != null) apps.push({ kind: 'eduContentTOC', id: tocId, depth: 0 });
  return apps;
}

// Builds the toc + LD applications for a chain. For each toc in the chain we
// emit one `eduContentTOC` application at the toc's depth. For each LD linked
// to any toc in the chain we emit one `learningDomain` application at the
// deepest toc's depth (the LD wins where the chain is deepest). When the chain
// is empty (TOCs not loaded yet) we fall back to depth-0 application of the
// raw toc/LD inputs so the directive still themes the host.
export function buildTocAndLdApplications(
  chain: readonly EduContentTOCInterface[],
  ldsByTocId: Record<number, number[]>,
  tocIdInput: number | undefined,
  learningDomainIdInput: number | undefined
): ScopeApplication[] {
  if (chain.length === 0) {
    const apps: ScopeApplication[] = [];
    if (tocIdInput != null) apps.push({ kind: 'eduContentTOC', id: tocIdInput, depth: 0 });
    if (learningDomainIdInput != null) apps.push({ kind: 'learningDomain', id: learningDomainIdInput, depth: 0 });
    return apps;
  }

  const apps: ScopeApplication[] = [];
  const ldDepthById = new Map<number, number>();

  for (const toc of chain) {
    const depth = toc.depth ?? 0;
    apps.push({ kind: 'eduContentTOC', id: toc.id, depth });

    // Skip multi-LD tocs: when a toc is linked to multiple LDs, two
    // `learningDomain` applications would land on the host at identical
    // specificity and the cascade would arbitrarily pick the last one. We
    // contribute no LD application for this toc; LDs already inherited from
    // ancestor tocs keep their ancestor depth.
    const lds = ldsByTocId[toc.id] ?? [];
    if (lds.length !== 1) continue;

    const ldId = lds[0];
    const existing = ldDepthById.get(ldId);
    if (existing === undefined || depth > existing) ldDepthById.set(ldId, depth);
  }

  // Honor an explicit learningDomainId input on top of the chain-derived LDs.
  // If the chain already added it (at some depth ≥ 0), keep that depth;
  // otherwise add it at depth 0.
  if (learningDomainIdInput != null && !ldDepthById.has(learningDomainIdInput)) {
    ldDepthById.set(learningDomainIdInput, 0);
  }

  for (const [id, depth] of ldDepthById) {
    apps.push({ kind: 'learningDomain', id, depth });
  }

  return apps;
}

export function pickDeepestTocId(
  applications: readonly ScopeApplication[],
  fallback: number | undefined
): number | undefined {
  let best: ScopeApplication | undefined;
  for (const a of applications) {
    if (a.kind !== 'eduContentTOC') continue;
    if (!best || a.depth > best.depth) best = a;
  }
  return best?.id ?? fallback;
}

// Walks the toc's ancestor chain (and includes the toc itself) using the
// nested-set bounds stored on each toc entity. Mirrors the `includeSelf: true`
// branch of `EduContentTocQueries.getAllAncestorsForToc`. Returns `[]` if the
// toc isn't in the dict yet (DAL not loaded for this book) — the directive
// then falls back to a depth-0 single-toc cascade.
//
// O(n) over all loaded tocs, but n is bounded by the tocs loaded for the
// current book/route — a handful in practice — so the scan cost is negligible.
export function buildAncestorChain(
  tocEntities: Dictionary<EduContentTOCInterface>,
  tocId: number
): EduContentTOCInterface[] {
  const target = tocEntities[tocId];
  if (!target) return [];

  const chain: EduContentTOCInterface[] = [];
  for (const toc of Object.values(tocEntities)) {
    if (!toc || toc.treeId !== target.treeId) continue;
    if (toc.id === tocId || (toc.lft < target.lft && toc.rgt > target.rgt)) chain.push(toc);
  }
  return chain;
}
